import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { postItem } from "../api/generics";
import { SUCCESS } from "../utils/constants/tags";

interface ConsentClient {
  host: string;
  name: string | null;
  redirectHost: string;
  loopbackOnly: boolean;
}

interface ConsentTaxPayer {
  id: number;
  ruc: string;
  socialName: string;
  fantasyName?: string;
}

interface ConsentBlock {
  reason: string;
  message: string;
}

interface ConsentDetails {
  client?: ConsentClient;
  scopes?: { scope: string; label: string }[];
  /** This assistant is already connected with everything it is asking for. */
  alreadyAuthorized?: boolean;
  taxPayers?: ConsentTaxPayer[];
  blocked?: ConsentBlock;
}

/**
 * The consent screen: one screen, one button, no form.
 *
 * What is being authorised is the person's SmartDoc account, not a company —
 * the same mental model as connecting a Drive: you connect the account, then
 * open whichever file you want. Which company a conversation works on is chosen
 * by talking, so there is nothing to tick here.
 *
 * The company list is shown as information for a reason. Asking at this moment
 * would be asking a question the person cannot answer: they do not yet know
 * what they are going to ask about. And the per company control already exists
 * and is better placed — it belongs to the owner of each company, not to
 * whoever they invited.
 *
 * Designed for a phone from the start, not adapted afterwards: this screen and
 * the login in front of it are going to be seen on small screens far more than
 * on large ones.
 */
const OauthConsentPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const request = params.get("request");

  // `/oauth/authorize` sends the browser here with the reason written out when
  // the request itself was broken — a redirect back to an unvalidated URI is
  // what would turn the authorization endpoint into an open redirector.
  const upstreamError = params.get("error_description") ?? params.get("error");

  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (request === null) {
        setLoading(false);
        return;
      }

      const response = await postItem<{ request: string }>(
        "/oauth/consent/details/",
        { request },
      );

      if (response.status !== SUCCESS) {
        setFailure(
          response.detail ??
            "No se pudo leer la solicitud. Volvé a conectar desde tu asistente.",
        );
      } else {
        setDetails(response.data as unknown as ConsentDetails);
      }

      setLoading(false);
    };

    load();
  }, [request]);

  const decide = useCallback(
    async (path: string) => {
      if (request === null) {
        return;
      }

      setDeciding(true);

      const response = await postItem<{ request: string }>(path, { request });

      if (response.status !== SUCCESS) {
        setFailure(response.detail ?? "No se pudo completar la autorización.");
        setDeciding(false);
        return;
      }

      // Back to the AI client, which is where the flow continues. A full
      // navigation and not a router push: the destination is not ours.
      const data = response.data as unknown as { redirectUrl: string };
      window.location.href = data.redirectUrl;
    },
    [request],
  );

  // An assistant that is already connected and asks for nothing new gets
  // through without the screen: it was consented to already, and asking again
  // is what had people approving the same connection five times in a row.
  const autoApprove = details?.alreadyAuthorized === true;

  useEffect(() => {
    if (autoApprove) {
      decide("/oauth/consent/approve/");
    }
  }, [autoApprove, decide]);

  const frame = (children: JSX.Element) => (
    <div className="c-app c-default-layout flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol xs="12" md="8" lg="6">
            <CCard className="p-2 p-md-4">
              <CCardBody>
                {/* Our logo, on our own screen: the page the assistant opens
                    has to be recognisable as SmartDoc at a glance. It never
                    stands in for the client, which is still named by host. */}
                <div className="text-center mb-3">
                  <img src="/SmartDoc.png" alt="SmartDoc" height={56} className="theme-logo" />
                </div>
                {children}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );

  if (upstreamError !== null) {
    return frame(
      <>
        <h5>No se pudo iniciar la conexión</h5>
        <CAlert color="danger" className="mb-0">
          {upstreamError}
        </CAlert>
      </>,
    );
  }

  if (request === null) {
    return frame(
      <>
        <h5>No hay ninguna conexión pendiente</h5>
        <p className="text-muted mb-0">
          Esta pantalla se abre sola cuando conectás SmartDoc desde tu asistente
          de IA.
        </p>
      </>,
    );
  }

  if (loading) {
    return frame(<p className="text-muted mb-0">Cargando…</p>);
  }

  if (failure !== null) {
    return frame(
      <>
        <h5>No se pudo completar</h5>
        <CAlert color="danger" className="mb-0">
          {failure}
        </CAlert>
      </>,
    );
  }

  if (details?.blocked !== undefined) {
    return frame(
      <>
        <h5>No se puede conectar una IA con esta cuenta</h5>
        <p className="mb-0">{details.blocked.message}</p>
      </>,
    );
  }

  const client = details?.client;
  const taxPayers = details?.taxPayers ?? [];
  const canWrite =
    details?.scopes?.some((entry) => entry.scope === "documents:write") ===
    true;

  if (autoApprove) {
    return frame(
      <p className="text-muted mb-0">
        Ya autorizaste a {client?.host ?? "este asistente"}. Volviendo…
      </p>,
    );
  }

  return frame(
    <>
      {/* The host, never the self asserted name on its own: a client metadata
          document can say whatever it likes about itself, and the host is the
          only part of it that cannot be faked. */}
      <h5 className="mb-1">
        {client?.host ?? "Una aplicación"} quiere conectarse a tu cuenta de
        SmartDoc
      </h5>
      <p className="text-muted small mb-4">
        {client?.name !== null && client?.name !== undefined
          ? `${client.name} · `
          : ""}
        vuelve a {client?.redirectHost}
      </p>

      {/* Whether it can issue depends on what the client asked for, so the
          sentence does too: saying it cannot issue would be false for a client
          that asked for write, and saying it can would scare off one that did
          not. */}
      <p>
        Va a poder{" "}
        <strong>ver tu facturación, tus documentos y tus reportes</strong>, con
        los mismos permisos que tenés vos en cada empresa.{" "}
        {canWrite ? (
          <>
            También va a poder{" "}
            <strong>
              preparar facturas y emitirlas, sólo cuando vos se lo confirmes
            </strong>
            .{" "}
          </>
        ) : (
          <strong>No puede emitir documentos. </strong>
        )}
        <strong>Nunca anula documentos.</strong>
      </p>

      {details?.scopes !== undefined && details.scopes.length > 1 && (
        <ul className="pl-4">
          {details.scopes.map((entry) => (
            <li key={entry.scope}>{entry.label}</li>
          ))}
        </ul>
      )}

      <p className="mb-1">
        Trabaja con <strong>una empresa a la vez</strong>, y vas a elegir cuál
        conversando. Hoy tenés acceso con IA habilitada en:
      </p>
      <ul className="pl-4">
        {taxPayers.map((taxPayer) => (
          <li key={taxPayer.id}>
            {taxPayer.socialName} — {taxPayer.ruc}
          </li>
        ))}
      </ul>
      <p className="text-muted small">
        Las empresas que se te agreguen después van a estar disponibles
        automáticamente.
      </p>

      {/* A metadata document cannot stop a local process from binding a port
          and passing itself off as the legitimate client, so this is said out
          loud rather than left for the person to notice in the host. */}
      {client?.loopbackOnly === true && (
        <CAlert color="warning" className="small">
          Esta conexión vuelve a un programa que corre en tu propia computadora
          ({client.redirectHost}). Autorizala sólo si vos iniciaste esto.
        </CAlert>
      )}

      <p className="text-muted small">
        Las respuestas se procesan en {client?.host ?? "el proveedor de IA"}, el
        proveedor de IA que elegiste. Podés desconectar cuando quieras desde Mi
        cuenta → Conexiones de IA.
      </p>

      <div className="d-flex flex-column-reverse flex-sm-row justify-content-end">
        <CButton
          color="secondary"
          variant="outline"
          disabled={deciding}
          className="mt-2 mt-sm-0 mr-sm-2"
          onClick={() => decide("/oauth/consent/deny/")}
        >
          Cancelar
        </CButton>
        <CButton
          color="primary"
          disabled={deciding}
          onClick={() => decide("/oauth/consent/approve/")}
        >
          {deciding ? "Conectando…" : "Autorizar"}
        </CButton>
      </div>
    </>,
  );
};

export default OauthConsentPage;
